import React, { useState, useEffect, useRef } from 'react';
import { Select, Pagination, Divider, Spin } from 'clad';
import { debounce } from 'utils';

const { Option } = Select;

const SelectBase = React.forwardRef((props, ref) => {
    const {
        schema,
        value,
        placeholder,
        mode,
        fetch,
        onChange,
        labelInValue,
        pageSize
    } = props;
    const [data, setData] = useState({
        data: null,
        count: null,
        loading: null,
        loaded: false
    });
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: pageSize || 10
    });
    const [open, setOpen] = useState(false);
    const [isSelected, setIsSelected] = useState(false);

    const isMounted = useRef(null);
    const divElContainerSelect = useRef(null);
    const divElContainerPagination = useRef(null);
    const textLoading = _('Loading...');

    const handleFetch = async ({ query, id, pagination }) => {
        let result;

        if (isMounted.current) {
            setData({
                ...data,
                loading: true
            });

            if (labelInValue && id) {
                const parseIds = id.map(value => value.key);
                result = await fetch({ query, id: parseIds, pagination });
            } else {
                result = await fetch({ query, id, pagination });
            }

            // claude: re-check mount after the await; the component may have
            // unmounted while the fetch was in flight (e.g. modal closed on
            // save), and setData on an unmounted component warns/leaks
            if (isMounted.current) {
                setData({
                    ...result,
                    loading: false,
                    loaded: true
                });
            }
        }
    };

    const options = () => {
        const { idOption, option } = schema;

        return data.data.map(row => {
            const icon = row.icon ? (
                <img style={{ verticalAlign: 'text-bottom' }} src={row.icon} />
            ) : null;
            const displayItemStyle = row.missingSelected ? 'none' : 'block';
            return (
                <Option
                    style={{ display: displayItemStyle }}
                    key={row[idOption]}
                    value={row[idOption]}
                >
                    {icon} <span>{row[option]}</span>
                </Option>
            );
        });
    };

    const renderOptions = () => {
        if (data.data !== null) {
            if (props.renderOptions) {
                return props.renderOptions(data.data);
            }
            return options();
        }
    };

    const renderPagination = () => (
        <React.Fragment>
            <Divider style={{ margin: '4px 0' }} />
            <div style={{ padding: '8px' }}>
                <Pagination
                    style={{ marginBottom: '4px' }}
                    simple
                    size="small"
                    total={data.count}
                    defaultCurrent={pagination.page}
                    pageSize={pagination.pageSize}
                    onChange={handlePagination}
                />
            </div>
        </React.Fragment>
    );

    const renderDropDown = menu => {
        return data.loading || data.loading === null ? (
            <Spin tip={textLoading}>
                <div>
                    {menu}
                    {renderPagination()}
                </div>
            </Spin>
        ) : (
            <div ref={divElContainerPagination}>
                {menu}
                {pagination.pageSize > 1 && renderPagination()}
            </div>
        );
    };

    const handleDropDown = () => {
        setOpen(true);
    };

    const handlePagination = (page, pageSize) => {
        const pagination = { page, pageSize };
        handleFetch({ query: null, id: value, pagination });
        setPagination({ page, pageSize });
    };

    const handleChange = value => {
        if (onChange) {
            onChange(value);
        }
    };

    const setSearchQueryDebounce = debounce(value => {
        handleFetch({
            query: value,
            id: undefined,
            pagination: { page: 1, pageSize: 10 }
        });
    }, 300);

    const handleSearch = value => {
        setSearchQueryDebounce(value);
    };

    const handleSelect = value => {
        if (!mode || mode === 'default') {
            setIsSelected(true);
        }
    };

    const checkDropDownMenu = e => {
        const containerPagination = divElContainerPagination.current;
        const containerSelect = divElContainerSelect.current;
        if (containerPagination) {
            if (containerPagination.contains) {
                if (
                    !containerPagination.contains(e.target) &&
                    !containerSelect.contains(e.target)
                ) {
                    setOpen(false);
                }
            }
        }
    };

    useEffect(() => {
        isMounted.current = true;

        if (data.data === null && !props.data) {
            handleFetch({ query: null, id: value, pagination });
        } else {
            if (isMounted.current) {
                setData({
                    ...props.data,
                    loading: false,
                    loaded: true
                });
            }
        }
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Claude Code: Watch for value changes and refetch to load selected item
    useEffect(
        () => {
            if (isMounted.current && data.loaded && value !== undefined && value !== null) {
                handleFetch({ query: null, id: value, pagination });
            }
        },
        [value, data.loaded]
    );

    useEffect(
        () => {
            if (isMounted.current) {
                setOpen(false);
                setIsSelected(false);
            }
        },
        [isSelected]
    );

    useEffect(() => {
        document.addEventListener('click', checkDropDownMenu);
        return () => {
            window.removeEventListener('click', checkDropDownMenu);
        };
    });

    return (
        <div ref={divElContainerSelect}>
            <Select
                mode={mode}
                placeholder={!data.loaded ? textLoading : placeholder}
                style={{ width: '100%' }}
                value={!data.loaded ? undefined : value}
                loading={data.loading}
                open={open}
                showSearch
                dropdownRender={renderDropDown}
                onDropdownVisibleChange={handleDropDown}
                onSearch={handleSearch}
                onChange={handleChange}
                onSelect={handleSelect}
                labelInValue={labelInValue || false}
                filterOption={false}
            >
                {renderOptions()}
            </Select>
        </div>
    );
});

export default SelectBase;
